import { API, getTenantMember, Token } from '@kudos/http-client'
import { isAxiosError } from 'axios'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

const PERSIST_KEY = 'session-storage'

export const TENANT_SESSION_REGEX = /^session-[^-]{8}-[^-]{8}$/

const isTenantSession = (sessionKey: string): boolean => {
  // Rough heuristic: tenant sessions include user and tenant ids -> two 8-char segments
  return TENANT_SESSION_REGEX.test(sessionKey)
}

/**
 * Extracts userId and tenantId from a tenant session string
 * @param session - Session string in format "session-<userId>-<tenantId>"
 * @returns Object with userId and tenantId, or null if invalid format
 */
const extractIdsFromSession = (session: string): { userId: string; tenantId: string } | null => {
  const sessionParts = session.split('-')
  if (sessionParts.length === 3 && sessionParts[0] === 'session') {
    return {
      userId: sessionParts[1],
      tenantId: sessionParts[2],
    }
  }
  return null
}

/**
 * Handles getTenantMember errors consistently across the auth store
 * Only clears auth on authentication errors (401, 403), preserves session on server errors
 */
const handleGetTenantMemberError = (
  error: unknown,
  session: string | null,
  clearAuth: () => void,
  setIds: (ids: { userId: string; tenantId: string }) => void,
) => {
  // Only clear auth for authentication errors (401, 403), not server errors (500, 502, 503, etc.)
  if (isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
    clearAuth()
  } else {
    // For server errors, extract tenantId/userId from session if possible
    if (session && isTenantSession(session)) {
      const ids = extractIdsFromSession(session)
      if (ids) {
        setIds(ids)
      }
    }
  }
}

type AuthStoreState = {
  session: string | null
  tenantId: string | null
  userId: string | null
  tenantMemberId: string | null
  isLoading: boolean
  initialize: (accessToken: Token, options?: { persist?: boolean; skipMe?: boolean }) => void
  initializeFromStorage: () => Promise<void>
  clearAuth: (resetStorage?: boolean) => void
  clearStorage: () => void
}

export const useAuthStore = create<AuthStoreState>()(
  persist(
    (set, get) => ({
      session: null,
      tenantId: null,
      tenantMemberId: null,
      userId: null,
      isLoading: true,

      clearStorage: () => {
        try {
          window.localStorage.removeItem(PERSIST_KEY)
        } catch {
          /* intentionally ignore storage cleanup errors */
        }
      },

      clearAuth: (resetStorage = true) => {
        delete API.defaults.headers.common['Authorization']
        set({ session: null, tenantId: null, userId: null, tenantMemberId: null, isLoading: false })
        if (resetStorage) get().clearStorage()
      },

      initialize: (accessToken, options) => {
        set({ isLoading: true })
        const shouldPersist = options?.persist !== undefined ? options.persist : true
        if (shouldPersist && accessToken.session) set({ session: accessToken.session })
        API.defaults.headers.common['Authorization'] = accessToken.session

        if (!options?.skipMe) {
          getTenantMember('me', { embeds: [] })
            .then(member =>
              set({ tenantId: member.tenantId, userId: member.userId, tenantMemberId: member.id }),
            )
            .finally(() => set({ isLoading: false }))
        } else {
          set({ isLoading: false })
        }
      },

      initializeFromStorage: async () => {
        set({ isLoading: true })
        const session = get().session
        if (session) {
          API.defaults.headers.common['Authorization'] = session
          if (isTenantSession(session)) {
            try {
              const member = await getTenantMember('me', { embeds: [] })
              set({ tenantId: member.tenantId, userId: member.userId, tenantMemberId: member.id })
            } catch (error: unknown) {
              handleGetTenantMemberError(
                error,
                session,
                () => get().clearAuth(false),
                // Set tenantMemberId to null at this point as we only have the userId and tenantId
                // from the sessions token. tenantMemberId is not included in that.
                ids => set({ tenantId: ids.tenantId, userId: ids.userId, tenantMemberId: null }),
              )
            }
          }
          set({ isLoading: false })
          return
        }

        get().clearAuth()
        set({ isLoading: false })
      },
    }),
    {
      name: PERSIST_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({ session: state.session }),
      onRehydrateStorage: () => state => {
        const session = state?.session
        if (session) {
          API.defaults.headers.common['Authorization'] = session
        }
      },
    },
  ),
)
